import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '../../services/api';
import { FORMAT_LABELS, isVideoDeliveryType } from '../../config/deliveryTypes';
import { PlatformIcon, PLATFORM_COLORS } from '../shared/PlatformIcon';
import { composeCaption } from '../../utils/captionHashtags';

const DM_TEXT_LIMIT = 2000;

interface Asset {
  id: string;
  title: string;
  type: string;
  status: string;
  url?: string;
  previewUrl?: string;
  caption?: string;
  manifest?: any;
}

interface Props {
  assets: Asset[];
  jobId: string;
  captionSeo?: any;
  onRefresh: () => void;
}


// Rótulos dos tipos de ENTREGA vêm do registro único (config/deliveryTypes.ts); aqui
// ficam só os tipos de ASSET que não são deliveryType (slides, vídeo/imagem avulsos).
const TYPE_LABELS: Record<string, string> = {
  ...FORMAT_LABELS,
  carousel_slides: 'Slides Carrossel',
  video: 'Video',
  image: 'Imagem',
};

// Retorna datetime local no formato "YYYY-MM-DDTHH:mm" para o input datetime-local
function localDatetimeDefault() {
  const d = new Date(Date.now() + 60 * 60 * 1000); // +1h
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function PublishModal({
  asset, allAssets, captionSeo, onClose, onPublished, marketingJobId, music, aiGeneratedDefault = false,
}: {
  asset: Asset; allAssets: Asset[]; captionSeo?: any; onClose: () => void; onPublished: () => void;
  // Estado inicial do rotulo "feito com IA" (Instagram isAiGenerated, TikTok
  // videoMadeWithAi, YouTube containsSyntheticMedia). Começa desligado também
  // para conteúdo criado pelo agente. O usuário pode ativar no modal.
  aiGeneratedDefault?: boolean;
  // Produção que originou esta publicação, quando o modal foi aberto a partir de
  // uma. É o que faz o card em Produções saber que já foi postada e onde.
  marketingJobId?: string;
  // Trilha do catálogo do Instagram escolhida na produção (IA ou usuário). Não
  // está dentro do arquivo de vídeo: é anexada agora, no ato de publicar, e só
  // vale para Reels. Ver instagramAudioService no backend.
  music?: {
    audioId: string;
    title?: string | null;
    artist?: string | null;
    volume?: number;
    enabled?: boolean;
  } | null;
}) {
  const [groups, setGroups] = useState<any[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Extrai caption e hashtags do texto em prosa quando o LLM nao retornou JSON estruturado
  function extractFromProse(prose: string): { caption: string; hashtags: string } {
    let caption = '';
    let hashtags = '';
    const captionMatch = prose.match(/(?:Legenda|Caption)[^:\n]*:\s*\n+([\s\S]+?)(?=\n\n|\*\*Hashtags|\*\*Promise|---|\n\*\*|$)/i);
    if (captionMatch) caption = captionMatch[1].replace(/\*\*/g, '').trim();
    if (!caption) {
      const lines = prose.split('\n').map(l => l.trim()).filter(l => l && !l.startsWith('#') && !l.startsWith('**') && !l.startsWith('-') && !l.startsWith('|') && !l.startsWith('>'));
      if (lines.length > 0) caption = lines[0];
    }
    const hashMatch = prose.match(/(?:Hashtags)[^:\n]*:\s*\n+(#[^\n]+)/i);
    if (hashMatch) hashtags = hashMatch[1].trim();
    if (!hashtags) {
      const hashLine = prose.split('\n').find(l => /^#[a-zA-ZA-u]/.test(l.trim()));
      if (hashLine) hashtags = hashLine.trim();
    }
    return { caption, hashtags };
  }

  const prose = !captionSeo?.caption && captionSeo?.text ? captionSeo.text : null;
  const fromProse = prose ? extractFromProse(prose) : null;

  function normalizeCaptionValue(value: any, platform?: string): string {
    if (!value) return '';
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
        try {
          return normalizeCaptionValue(JSON.parse(trimmed), platform);
        } catch {}
      }
      return trimmed;
    }
    if (typeof value === 'object') {
      const byPlatform = value.captionByPlatform || value.byPlatform || null;
      const key = String(platform || '').toLowerCase();
      return String(
        (key && byPlatform?.[key]) ||
        value.caption ||
        value.text ||
        value.captionShort ||
        ''
      ).trim();
    }
    return String(value).trim();
  }

  // Caption: prefere asset.caption ? captionSeo.caption ? extraido da prosa
  const rawCaption = normalizeCaptionValue(asset.caption) || normalizeCaptionValue(captionSeo) || normalizeCaptionValue(captionSeo?.caption) || fromProse?.caption || '';
  // Hashtags: captionSeo.hashtagsFlat ? extraido da prosa
  const rawHashtags = captionSeo?.hashtagsFlat || fromProse?.hashtags || '';
  // A legenda e o bloco de hashtags são campos separados aqui e são concatenados
  // na publicação. Caption que já vem com o bloco no fim (asset antigo, gerado
  // antes da normalização do backend, ou legenda vinda da prosa) sairia com as
  // tags duplicadas no feed — separa e une sem repetir.
  const initial = composeCaption(rawCaption, rawHashtags);
  const initialCaption = initial.body;
  const initialHashtags = initial.tagLine;

  const [caption, setCaption] = useState(initialCaption);
  // TikTok publica só este campo (customContent), sem o bloco de hashtags. Sem
  // valor do modelo, cai na legenda cortada em 90 para o campo nunca ficar vazio
  // e travar a publicação quando o TikTok é a única conta selecionada.
  const [tiktokCaption, setTiktokCaption] = useState(
    captionSeo?.captionByPlatform?.tiktok || initialCaption.slice(0, 90)
  );
  const [hashtags, setHashtags] = useState(initialHashtags);
  const [mode, setMode] = useState<'now' | 'schedule'>('schedule');
  const [scheduledAt, setScheduledAt] = useState(localDatetimeDefault());
  const [publishing, setPublishing] = useState(false);

  // Automacao de comentario para DM
  const rawAuto = captionSeo?.automationSpec || null;
  const [autoEnabled, setAutoEnabled] = useState(!!rawAuto);
  const [autoKeywords, setAutoKeywords] = useState<string>((rawAuto?.keywords || []).join(', '));
  const [autoDmMessage, setAutoDmMessage] = useState(rawAuto?.dmMessage || '');
  const [autoCommentReply, setAutoCommentReply] = useState(rawAuto?.commentReply || 'Te chamei no Direct!');
  const [autoName, setAutoName] = useState(rawAuto?.name || '');
  const [followerCheckEnabled, setFollowerCheckEnabled] = useState(false);
  const [followerCheckPromptMessage, setFollowerCheckPromptMessage] = useState('Clique no botao abaixo para liberar seu acesso.');
  const [followerCheckButtonText, setFollowerCheckButtonText] = useState('CHECAR');
  const [followerCheckFollowerMessage, setFollowerCheckFollowerMessage] = useState('Obrigado por seguir. Aqui esta o que prometemos.');
  const [followerCheckNonFollowerMessage, setFollowerCheckNonFollowerMessage] = useState('Para receber o material, siga nosso perfil e responda CHECAR novamente.');

  const isCarousel = asset.type === 'carrossel';
  const isVideo = asset.type?.includes('video') ?? false;
  const isStatic = !isCarousel && !isVideo;

  // Warnings shown after selecting accounts (platform + type specific)
  const FORMAT_WARNINGS: Record<string, Record<string, string>> = {
    instagram: {
      reels: '9:16 obrigatorio (1080x1920). Videos em outra proporcao serao recortados.',
      story: '9:16 obrigatorio. Max 15s por story.',
      feed: 'Proporcao entre 4:5 e 1.91:1. Fora disso sera cortado.',
      carousel: 'Todos os slides devem ter a mesma proporcao.',
    },
    facebook: {
      reels: '9:16 recomendado. Outra proporcao pode aparecer com barras.',
      story: '9:16 obrigatorio.',
      feed: 'Proporcao livre, mas 1:1 ou 4:5 performam melhor.',
      carousel: 'Apenas imagens (sem video no carrossel do Facebook).',
    },
    tiktok: {
      feed: '9:16 obrigatorio. Videos horizontais ficam com barras pretas.',
      carousel: 'Max 35 fotos. Proporcao 9:16 recomendada.',
      photo: 'Foto unica. Proporcao 9:16 recomendada. O texto vira o titulo (max 90 caracteres).',
    },
    youtube: {
      video: '16:9 obrigatorio. Videos verticais ficam com barras laterais grandes.',
      shorts: '9:16 obrigatorio (1080x1920). Detectado automaticamente se < 3 min.',
    },
    twitter: {
      feed: 'Video 9:16 pode exibir barras laterais. Max 2min20s.',
    },
    linkedin: {
      feed: 'Video prefere 16:9. Proporcao 9:16 pode aparecer cortada.',
    },
    threads: {
      feed: 'Max 5 min de video.',
      carousel: 'Max 10 imagens.',
    },
    telegram: {
      feed: 'Max 50MB para video.',
      album: 'Max 10 itens. Imagens e videos podem ser misturados.',
    },
    reddit: {
      feed: 'Apenas 1 imagem por post. Titulo obrigatorio.',
    },
    pinterest: {
      feed: 'Proporcao 2:3 (1000x1500px) recomendada. Outras proporcoes serao cortadas.',
    },
    snapchat: {
      story: '9:16 obrigatorio (1080x1920). Max 10s por snap.',
      spotlight: '9:16 obrigatorio. Max 60s.',
    },
    googlebusiness: {
      feed: 'Apenas 1 imagem por post. Videos nao sao aceitos.',
    },
    bluesky: {
      feed: 'Max 4 imagens. Cada imagem comprimida automaticamente para = 1MB.',
    },
  };

  // Post type options per platform based on asset type
  const POST_TYPE_OPTIONS: Record<string, Array<{ id: string; label: string; apiContentType?: string }>> = isCarousel ? {
    instagram: [{ id: 'carousel', label: 'Carrossel', apiContentType: 'carousel' }],
    facebook: [{ id: 'carousel', label: 'Carrossel', apiContentType: 'carousel' }],
    tiktok: [{ id: 'carousel', label: 'Carrossel de Fotos' }],
    threads: [{ id: 'carousel', label: 'Carrossel', apiContentType: 'carousel' }],
    linkedin: [{ id: 'feed', label: 'Post (multiplas imagens)' }],
    telegram: [{ id: 'album', label: 'Album', apiContentType: 'album' }],
  } : isVideo ? {
    instagram: [{ id: 'reels', label: 'Reels' }, { id: 'story', label: 'Story', apiContentType: 'story' }, { id: 'feed', label: 'Feed' }],
    facebook: [{ id: 'reels', label: 'Reels', apiContentType: 'reel' }, { id: 'story', label: 'Story', apiContentType: 'story' }, { id: 'feed', label: 'Feed' }],
    tiktok: [{ id: 'feed', label: 'Video' }],
    youtube: [{ id: 'video', label: 'Video' }, { id: 'shorts', label: 'Shorts' }],
    snapchat: [{ id: 'spotlight', label: 'Spotlight', apiContentType: 'spotlight' }, { id: 'story', label: 'Story', apiContentType: 'story' }],
    twitter: [{ id: 'feed', label: 'Tweet' }],
    linkedin: [{ id: 'feed', label: 'Post' }],
    threads: [{ id: 'feed', label: 'Thread' }],
    bluesky: [{ id: 'feed', label: 'Post' }],
    reddit: [{ id: 'feed', label: 'Post' }],
    pinterest: [{ id: 'feed', label: 'Pin' }],
    telegram: [{ id: 'feed', label: 'Mensagem' }],
    googlebusiness: [{ id: 'feed', label: 'Post' }],
  } : {
    instagram: [{ id: 'feed', label: 'Feed' }, { id: 'story', label: 'Story', apiContentType: 'story' }],
    facebook: [{ id: 'feed', label: 'Feed' }, { id: 'story', label: 'Story', apiContentType: 'story' }],
    // Foto unica no TikTok e o mesmo photo post do carrossel, com um item so.
    tiktok: [{ id: 'photo', label: 'Foto' }],
    snapchat: [{ id: 'story', label: 'Story', apiContentType: 'story' }],
    twitter: [{ id: 'feed', label: 'Tweet' }],
    linkedin: [{ id: 'feed', label: 'Post' }],
    threads: [{ id: 'feed', label: 'Thread' }],
    bluesky: [{ id: 'feed', label: 'Post' }],
    reddit: [{ id: 'feed', label: 'Post' }],
    pinterest: [{ id: 'feed', label: 'Pin' }],
    telegram: [{ id: 'feed', label: 'Mensagem' }],
    googlebusiness: [{ id: 'feed', label: 'Post' }],
  };

  const [platformPostTypes, setPlatformPostTypes] = useState<Record<string, string>>({});
  // Rotulo de conteudo gerado por IA. Um toggle so: vira o campo certo de cada rede.
  const [aiGenerated, setAiGenerated] = useState(aiGeneratedDefault);

  // Instagram
  const [instagramTrialReel, setInstagramTrialReel] = useState(false);
  const [instagramGraduation, setInstagramGraduation] = useState<'MANUAL' | 'SS_PERFORMANCE'>('SS_PERFORMANCE');
  const [instagramThumbnail, setInstagramThumbnail] = useState('');

  // TikTok
  const [tiktokAutoMusic, setTiktokAutoMusic] = useState(true);
  const [tiktokPrivacy, setTiktokPrivacy] = useState<'PUBLIC_TO_EVERYONE' | 'MUTUAL_FOLLOW_FRIENDS' | 'SELF_ONLY'>('PUBLIC_TO_EVERYONE');
  const [tiktokDuet, setTiktokDuet] = useState(true);
  const [tiktokStitch, setTiktokStitch] = useState(true);
  const [tiktokDisclose, setTiktokDisclose] = useState(false);
  const [tiktokBrandContent, setTiktokBrandContent] = useState(false);
  const [tiktokBrandOrganic, setTiktokBrandOrganic] = useState(false);

  // YouTube
  const [youtubePrivacy, setYoutubePrivacy] = useState<'PUBLIC' | 'UNLISTED' | 'PRIVATE'>('PUBLIC');
  const [youtubeCategoryId, setYoutubeCategoryId] = useState('');
  const [youtubeTags, setYoutubeTags] = useState('');
  const [youtubeTitle, setYoutubeTitle] = useState('');

  // LinkedIn
  const [linkedinAuthorType, setLinkedinAuthorType] = useState<'PERSONAL' | 'ORGANIZATION'>('PERSONAL');
  const [linkedinOrgId, setLinkedinOrgId] = useState('');

  // Reddit
  const [redditSubreddit, setRedditSubreddit] = useState('');
  const [redditTitle, setRedditTitle] = useState('');
  const [redditFlair, setRedditFlair] = useState('');

  // Pinterest
  const [pinterestBoardId, setPinterestBoardId] = useState('');
  const [pinterestBoardSection, setPinterestBoardSection] = useState('');
  const [pinterestLink, setPinterestLink] = useState('');
  const [pinterestTitle, setPinterestTitle] = useState('');

  // Plataformas que nao suportam posts sociais
  const POSTING_UNSUPPORTED = new Set(['whatsapp', 'whatsapp_official', 'waba']);

  // Plataformas compativeis com carrossel
  const CAROUSEL_SUPPORTED = new Set(['instagram', 'facebook', 'tiktok', 'threads', 'linkedin', 'telegram']);
  // Plataformas incompativeis com video
  const VIDEO_UNSUPPORTED = new Set(['reddit', 'googlebusiness']);
  // Plataformas incompativeis com imagem estatica.
  //
  // O TikTok saiu daqui: o photo post dele aceita de 1 a 35 imagens (a doc da
  // Zernio chama o modo inteiro de "Photo Carousel", mas nao exige minimo de 2 —
  // e a API do TikTok fala em "a valid photo", no singular).
  const IMAGE_UNSUPPORTED = new Set(['youtube', 'snapchat']);

  const isPlatformCompatible = (platform: string): { ok: boolean; note?: string } => {
    const p = platform?.toLowerCase();
    if (POSTING_UNSUPPORTED.has(p)) return { ok: false, note: 'nao suporta posts' };
    if (isCarousel && !CAROUSEL_SUPPORTED.has(p)) return { ok: false, note: 'nao aceita carrossel' };
    if (isVideo && VIDEO_UNSUPPORTED.has(p)) return { ok: false, note: 'nao aceita video' };
    if (isStatic && IMAGE_UNSUPPORTED.has(p)) return { ok: false, note: 'nao aceita imagem estatica' };
    return { ok: true };
  };

  // Derive selected platforms and their chosen post type
  const allAccountsFlat = groups.flatMap(g => g.accounts || []);
  const selectedPlatforms = [...selected].map(id => {
    const acc = allAccountsFlat.find((a: any) => a._id === id);
    return acc?.platform?.toLowerCase();
  }).filter(Boolean) as string[];
  const uniquePlatforms = [...new Set(selectedPlatforms)];
  // Vale para QUALQUER seleção com TikTok, inclusive TikTok sozinho: antes, com uma
  // única conta selecionada, o campo não aparecia e o TikTok recebia a legenda cheia
  // + bloco de hashtags — muito acima dos 90 chars que o feed nativo aceita.
  const needsTikTokCaption = uniquePlatforms.includes('tiktok');

  const getPostType = (platform: string) => {
    const opts = POST_TYPE_OPTIONS[platform] || [{ id: 'feed', label: 'Feed' }];
    return platformPostTypes[platform] || opts[0].id;
  };

  useEffect(() => {
    api.getPostingAccounts()
      .then(res => { if (res.success) setGroups(res.data || []); })
      .catch(() => toast.error('Nao foi possivel carregar contas'))
      .finally(() => setLoadingAccounts(false));
  }, []);

  const toggleAccount = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Monta mediaItems: para carrossel, capa + todos os slides em ordem
  const buildMediaItems = () => {
    const mediaType = asset.type?.includes('video') ? 'video' : 'image';
    if (!isCarousel) {
      return asset.url ? [{ url: asset.url, type: mediaType }] : [];
    }
    // Carousel: capa (este asset) + slides do asset carousel_slides
    const slidesAsset = allAssets.find(a => a.type === 'carousel_slides');
    const slideUrls: string[] = (slidesAsset?.manifest?.slideAssets || [])
      .sort((a: any, b: any) => (a.slideNumber ?? 0) - (b.slideNumber ?? 0))
      .map((s: any) => s.url)
      .filter(Boolean);
    const items = [
      ...(asset.url ? [{ url: asset.url, type: 'image' }] : []),
      ...slideUrls.map(url => ({ url, type: 'image' })),
    ];
    return items.slice(0, 10); // Instagram max 10
  };

  const handlePublish = async () => {
    if (selected.size === 0) return toast.error('Selecione pelo menos uma conta.');
    if (mode === 'schedule' && !scheduledAt) return toast.error('Defina a data e hora de publicacao.');
    if (needsTikTokCaption && !tiktokCaption.trim()) return toast.error('Preencha a caption curta do TikTok.');
    if (needsTikTokCaption && tiktokCaption.length > 90) return toast.error('Caption do TikTok deve ter no maximo 90 caracteres.');
    if (autoEnabled && followerCheckEnabled) {
      if (!selectedPlatforms.includes('instagram')) return toast.error('Checar seguidor exige uma conta Instagram selecionada.');
      if (!followerCheckPromptMessage.trim() || !followerCheckButtonText.trim() || !followerCheckFollowerMessage.trim() || !followerCheckNonFollowerMessage.trim()) {
        return toast.error('Preencha o texto do botao e as duas mensagens do fluxo de seguidor.');
      }
      if ([followerCheckPromptMessage, followerCheckFollowerMessage, followerCheckNonFollowerMessage].some(msg => msg.length > DM_TEXT_LIMIT)) {
        return toast.error(`Mensagens de DM devem ter no maximo ${DM_TEXT_LIMIT} caracteres.`);
      }
    }
    setPublishing(true);
    try {
      const allAccounts = groups.flatMap(g => g.accounts || []);
      const platforms = [...selected].map(id => {
        const acc = allAccounts.find((a: any) => a._id === id);
        const p = acc.platform?.toLowerCase();
        const chosenTypeId = getPostType(p);
        const opts = POST_TYPE_OPTIONS[p] || [];
        const chosenOpt = opts.find(o => o.id === chosenTypeId) || opts[0];
        const platformSpecificData: Record<string, any> = {};

        if (chosenOpt?.apiContentType) {
          platformSpecificData.contentType = chosenOpt.apiContentType;
        }
        // TikTok
        if (p === 'tiktok') {
          // Foto unica e carrossel sao o MESMO modo para o TikTok (photo post):
          // muda so a quantidade de itens em mediaItems.
          if (chosenTypeId === 'carousel' || chosenTypeId === 'photo') {
            platformSpecificData.mediaType = 'photo';
            if (tiktokAutoMusic) platformSpecificData.auto_add_music = true;
          }
          platformSpecificData.privacyLevel = tiktokPrivacy;
          platformSpecificData.duetEnabled = tiktokDuet;
          platformSpecificData.stitchEnabled = tiktokStitch;
          if (aiGenerated) platformSpecificData.videoMadeWithAi = true;
          if (tiktokDisclose) {
            platformSpecificData.discloseContent = true;
            platformSpecificData.brandContent = tiktokBrandContent;
            platformSpecificData.brandOrganic = tiktokBrandOrganic;
          }
        }
        // Instagram
        if (p === 'instagram') {
          if (chosenTypeId === 'reels' && instagramTrialReel) {
            platformSpecificData.trialParams = { graduationStrategy: instagramGraduation };
          }
          // Musica de catalogo so existe em Reels; story/carrossel recusam na criacao.
          if (chosenTypeId === 'reels' && music?.enabled !== false && music?.audioId) {
            platformSpecificData.audioConfiguration = {
              audioId: music.audioId,
              audioVolume: Math.round(Math.min(1, Math.max(0.01, music.volume ?? 0.1)) * 100),
              videoVolume: 100,
            };
          }
          if (instagramThumbnail.trim()) platformSpecificData.instagramThumbnail = instagramThumbnail.trim();
          if (aiGenerated) platformSpecificData.isAiGenerated = true;
        }
        // YouTube
        if (p === 'youtube') {
          platformSpecificData.privacyStatus = youtubePrivacy;
          if (aiGenerated) platformSpecificData.containsSyntheticMedia = true;
          if (youtubeTitle.trim()) platformSpecificData.title = youtubeTitle.trim();
          if (youtubeCategoryId.trim()) platformSpecificData.categoryId = youtubeCategoryId.trim();
          if (youtubeTags.trim()) platformSpecificData.tags = youtubeTags.split(',').map(t => t.trim()).filter(Boolean);
        }
        // LinkedIn
        if (p === 'linkedin') {
          platformSpecificData.authorType = linkedinAuthorType;
          if (linkedinAuthorType === 'ORGANIZATION' && linkedinOrgId.trim()) {
            platformSpecificData.organizationId = linkedinOrgId.trim();
          }
        }
        // Reddit
        if (p === 'reddit') {
          if (redditSubreddit.trim()) platformSpecificData.subredditId = redditSubreddit.trim();
          if (redditTitle.trim()) platformSpecificData.title = redditTitle.trim();
          if (redditFlair.trim()) platformSpecificData.flairId = redditFlair.trim();
        }
        // Pinterest
        if (p === 'pinterest') {
          if (pinterestBoardId.trim()) platformSpecificData.boardId = pinterestBoardId.trim();
          if (pinterestBoardSection.trim()) platformSpecificData.boardSectionId = pinterestBoardSection.trim();
          if (pinterestLink.trim()) platformSpecificData.link = pinterestLink.trim();
          if (pinterestTitle.trim()) platformSpecificData.title = pinterestTitle.trim();
        }

        return {
          platform: acc.platform,
          accountId: acc._id,
          ...(p === 'tiktok' && needsTikTokCaption ? { customContent: tiktokCaption.trim() } : {}),
          ...(Object.keys(platformSpecificData).length > 0 ? { platformSpecificData } : {}),
        };
      });

      // Última barreira contra hashtag duplicada: se o usuário digitou tags no fim
      // da legenda, elas não são anexadas de novo pelo campo de hashtags.
      const fullCaption = composeCaption(caption, hashtags).full;

      const mediaItems = buildMediaItems();

      // Converte datetime-local para ISO 8601 com offset BRT (-03:00)
      const scheduledFor = mode === 'schedule'
        ? new Date(scheduledAt).toISOString().replace('Z', '-03:00')
        : undefined;

      // Monta automationSpec se habilitado e plataforma suporta (IG/FB)
      const hasIgFb = platforms.some(p => ['instagram', 'facebook'].includes(p.platform?.toLowerCase()));
      if (autoEnabled && hasIgFb && !followerCheckEnabled && autoDmMessage.length > DM_TEXT_LIMIT) return toast.error(`Mensagem da DM deve ter no maximo ${DM_TEXT_LIMIT} caracteres.`);

      const automationSpec = (autoEnabled && hasIgFb && (followerCheckEnabled || autoDmMessage.trim())) ? {
        name: autoName.trim() || `Automacao ${new Date().toLocaleDateString('pt-BR')}`,
        keywords: autoKeywords.split(',').map(k => k.trim()).filter(Boolean),
        dmMessage: followerCheckEnabled ? followerCheckPromptMessage.trim() : autoDmMessage.trim(),
        commentReply: autoCommentReply.trim() || undefined,
        // profileId e accountId serao resolvidos pelo backend via plataformas selecionadas
        profileId: groups.find(g => g.accounts?.some((a: any) => platforms.some(p => p.accountId === a._id)))?.lateProfileId || '',
        accountId: [...selected].find(id => {
          const acc = groups.flatMap(g => g.accounts || []).find((a: any) => a._id === id);
          return acc && ['instagram', 'facebook'].includes(acc.platform?.toLowerCase());
        }) || '',
        followerCheck: followerCheckEnabled && selectedPlatforms.includes('instagram') ? {
          enabled: true,
          promptMessage: followerCheckPromptMessage.trim(),
          buttonText: followerCheckButtonText.trim(),
          followerMessage: followerCheckFollowerMessage.trim(),
          nonFollowerMessage: followerCheckNonFollowerMessage.trim(),
        } : null,
      } : undefined;

      void api.createPost({
        content: fullCaption,
        platforms,
        mediaItems,
        timezone: 'America/Sao_Paulo',
        ...(mode === 'now' ? { publishNow: true } : { scheduledFor }),
        ...(automationSpec ? { automationSpec } : {}),
        ...(marketingJobId ? { marketingJobId } : {}),
      } as any).then(() => {
        onPublished();
      }).catch((err: any) => {
        toast.error(err.message || 'Erro ao enviar postagem para a API');
      });

      const msg = mode === 'now'
        ? 'Postagem enviada para processamento. Acompanhe o status em Postagens.'
        : `Post agendado para ${new Date(scheduledAt).toLocaleString('pt-BR')}`;
      toast.success(msg);
      onClose();
    } catch (err: any) {
      toast.error(err.message || 'Erro ao publicar');
    } finally {
      setPublishing(false);
    }
  };

  const mediaCount = buildMediaItems().length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-3 sm:p-4">
      <div className="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-slate-900 border border-slate-700 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-800 px-5 py-4">
          <div>
            <h3 className="font-bold text-slate-100">Publicar</h3>
            <p className="text-xs text-slate-500 mt-0.5 truncate">
              {isCarousel ? `Carrossel · ${mediaCount} imagens` : asset.title}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-300 text-sm leading-none">Fechar</button>
        </div>

        <div className="grid flex-1 grid-cols-1 gap-5 overflow-y-auto p-4 sm:p-5 lg:grid-cols-[minmax(0,1.12fr)_minmax(360px,0.88fr)]">

          {/* Contas */}
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">Contas</p>
            {loadingAccounts ? (
              <p className="text-sm text-slate-500 animate-pulse">Carregando contas...</p>
            ) : groups.length === 0 ? (
              <p className="text-sm text-slate-500 italic">Nenhuma conta conectada. Configure em Redes Sociais.</p>
            ) : (
              <div className="space-y-3">
                {groups.map((group: any) => (
                  <div key={group.groupId}>
                    <p className="text-[10px] font-semibold text-slate-600 uppercase tracking-wider mb-1.5">{group.groupName}</p>
                    <div className="flex flex-wrap gap-2">
                      {(group.accounts || [])
                        .filter((acc: any) => !POSTING_UNSUPPORTED.has(acc.platform?.toLowerCase()))
                        .map((acc: any) => {
                          const compat = isPlatformCompatible(acc.platform);
                          const isOn = selected.has(acc._id);
                          const colors = PLATFORM_COLORS[acc.platform?.toLowerCase()] || { bg: 'bg-slate-700', text: 'text-white' };
                          if (!compat.ok) {
                            return (
                              <div
                                key={acc._id}
                                title={compat.note}
                                className="flex items-center gap-2.5 rounded-xl border border-slate-800 px-3 py-2 text-sm font-medium opacity-35 cursor-not-allowed"
                              >
                                <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md ${colors.bg} ${colors.text} grayscale`}>
                                  <PlatformIcon platform={acc.platform} size={14} />
                                </span>
                                <span className="max-w-[120px] truncate text-slate-500">{acc.displayName || acc.username}</span>
                                {compat.note && <span className="text-[9px] text-slate-600 italic">{compat.note}</span>}
                              </div>
                            );
                          }
                          return (
                            <button
                              key={acc._id}
                              onClick={() => toggleAccount(acc._id)}
                              className={`flex items-center gap-2.5 rounded-xl border px-3 py-2 text-sm font-medium transition-all ${
                                isOn
                                  ? 'border-indigo-500 bg-indigo-900/30 text-slate-100 ring-1 ring-indigo-500/40'
                                  : 'border-slate-700 bg-slate-800 text-slate-300 hover:border-slate-500 hover:bg-slate-750'
                              }`}
                            >
                              <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md ${colors.bg} ${colors.text}`}>
                                <PlatformIcon platform={acc.platform} size={14} />
                              </span>
                              <span className="max-w-[140px] truncate">{acc.displayName || acc.username}</span>
                              {isOn && <span className="ml-auto text-[10px] text-indigo-400 font-bold">Selecionado</span>}
                            </button>
                          );
                        })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Formato por plataforma */}
          {uniquePlatforms.length > 0 && (
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">Formato por plataforma</p>
              <div className="space-y-2">
                {uniquePlatforms.map(platform => {
                  const opts = POST_TYPE_OPTIONS[platform];
                  if (!opts || opts.length <= 1) return null;
                  const chosen = getPostType(platform);
                  return (
                    <div key={platform} className="flex items-center gap-2 flex-wrap">
                      <span className="text-[11px] font-semibold text-slate-500 uppercase w-20 shrink-0">{platform}</span>
                      {opts.map(opt => (
                        <button
                          key={opt.id}
                          onClick={() => setPlatformPostTypes(prev => ({ ...prev, [platform]: opt.id }))}
                          className={`rounded-lg border px-3 py-1 text-xs font-medium transition-all ${
                            chosen === opt.id
                              ? 'border-indigo-500 bg-indigo-900/30 text-indigo-300'
                              : 'border-slate-700 bg-slate-800 text-slate-400 hover:border-slate-600'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  );
                })}
              </div>

              {/* Rotulo "feito com IA" — Instagram, TikTok e YouTube */}
              {uniquePlatforms.some(p => ['instagram', 'tiktok', 'youtube'].includes(p)) && (
                <div className="mt-3 rounded-xl border border-slate-700 bg-slate-800/50 p-3">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-xs font-semibold text-slate-300">Marcar como feito com IA</p>
                      <p className="text-[10px] text-slate-500 mt-0.5">
                        A rede exibe o aviso de conteudo gerado por IA. Vale para Instagram, TikTok e YouTube.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setAiGenerated(v => !v)}
                      className={`relative h-5 w-9 shrink-0 rounded-full transition-colors ${aiGenerated ? 'bg-indigo-600' : 'bg-slate-700'}`}
                    >
                      <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${aiGenerated ? 'translate-x-4' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                </div>
              )}

              {/* Trial Reels (Instagram) */}
              {uniquePlatforms.includes('instagram') && getPostType('instagram') === 'reels' && (
                <div className="mt-3 rounded-xl border border-slate-700 bg-slate-800/50 p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold text-slate-300">Reels de Teste (Trial Reels)</p>
                      <p className="text-[10px] text-slate-500 mt-0.5">Exibido apenas para nao-seguidores. Vira post oficial se performar bem.</p>
                    </div>
                    <button
                      onClick={() => setInstagramTrialReel(v => !v)}
                      className={`relative h-5 w-9 rounded-full transition-colors ${instagramTrialReel ? 'bg-indigo-600' : 'bg-slate-700'}`}
                    >
                      <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${instagramTrialReel ? 'translate-x-4' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                  {instagramTrialReel && (
                    <div className="mt-2">
                      <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Estrategia de graduacao</label>
                      <select
                        value={instagramGraduation}
                        onChange={e => setInstagramGraduation(e.target.value as any)}
                        className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-1.5 text-xs text-slate-200 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      >
                        <option value="SS_PERFORMANCE">Automatico (por performance)</option>
                        <option value="MANUAL">Manual (voce decide)</option>
                      </select>
                    </div>
                  )}
                </div>
              )}

              {/* Trilha do Instagram escolhida na producao. So aparece em Reels: e o
                  unico formato que aceita audio de catalogo. */}
              {music?.audioId && uniquePlatforms.includes('instagram') && getPostType('instagram') === 'reels' && (
                <div className="mt-3 rounded-xl border border-emerald-700/40 bg-emerald-900/20 p-3">
                  <p className="text-xs font-semibold text-emerald-200">Musica: {music.title || music.audioId}</p>
                  <p className="mt-0.5 text-[10px] text-slate-400">
                    {music.artist || 'Desconhecido'} - anexada ao Reel em {Math.round((music.volume ?? 0.1) * 100)}% de volume.
                    Se a faixa sair do catalogo ate a publicacao, o post falha em vez de sair com outro audio.
                  </p>
                </div>
              )}

              {/* TikTok auto musica (qualquer photo post: foto unica ou carrossel) */}
              {uniquePlatforms.includes('tiktok') && (isCarousel || isStatic) && (
                <div className="mt-3 rounded-xl border border-slate-700 bg-slate-800/50 p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold text-slate-300">TikTok: musica automatica</p>
                      <p className="text-[10px] text-slate-500 mt-0.5">Permite o TikTok adicionar uma musica automaticamente ao post de foto.</p>
                    </div>
                    <button
                      onClick={() => setTiktokAutoMusic(v => !v)}
                      className={`relative h-5 w-9 rounded-full transition-colors ${tiktokAutoMusic ? 'bg-indigo-600' : 'bg-slate-700'}`}
                    >
                      <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${tiktokAutoMusic ? 'translate-x-4' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                </div>
              )}
              {/* YouTube settings */}
              {uniquePlatforms.includes('youtube') && (
                <div className="mt-3 rounded-xl border border-slate-700 bg-slate-800/50 p-3 space-y-3">
                  <p className="text-xs font-semibold text-slate-300">YouTube</p>
                  <div>
                    <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Titulo do video</label>
                    <input value={youtubeTitle} onChange={e => setYoutubeTitle(e.target.value)} placeholder="Titulo para o YouTube..." className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-1.5 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500" />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Privacidade</label>
                    <select value={youtubePrivacy} onChange={e => setYoutubePrivacy(e.target.value as any)} className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-1.5 text-xs text-slate-200 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                      <option value="PUBLIC">Publico</option>
                      <option value="UNLISTED">Nao listado</option>
                      <option value="PRIVATE">Privado</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Tags (separadas por virgula)</label>
                    <input value={youtubeTags} onChange={e => setYoutubeTags(e.target.value)} placeholder="marketing, negocios, dicas" className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-1.5 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500" />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">ID da categoria (opcional)</label>
                    <input value={youtubeCategoryId} onChange={e => setYoutubeCategoryId(e.target.value)} placeholder="ex: 22 (People & Blogs)" className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-1.5 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500" />
                  </div>
                </div>
              )}

              {/* LinkedIn settings */}
              {uniquePlatforms.includes('linkedin') && (
                <div className="mt-3 rounded-xl border border-slate-700 bg-slate-800/50 p-3 space-y-3">
                  <p className="text-xs font-semibold text-slate-300">LinkedIn</p>
                  <div>
                    <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Postar como</label>
                    <select value={linkedinAuthorType} onChange={e => setLinkedinAuthorType(e.target.value as any)} className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-1.5 text-xs text-slate-200 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                      <option value="PERSONAL">Pessoa (perfil pessoal)</option>
                      <option value="ORGANIZATION">Empresa (pagina)</option>
                    </select>
                  </div>
                  {linkedinAuthorType === 'ORGANIZATION' && (
                    <div>
                      <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">ID da organizacao</label>
                      <input value={linkedinOrgId} onChange={e => setLinkedinOrgId(e.target.value)} placeholder="ex: 12345678" className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-1.5 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500" />
                    </div>
                  )}
                </div>
              )}

              {/* Reddit settings */}
              {uniquePlatforms.includes('reddit') && (
                <div className="mt-3 rounded-xl border border-slate-700 bg-slate-800/50 p-3 space-y-3">
                  <p className="text-xs font-semibold text-slate-300">Reddit</p>
                  <div>
                    <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Subreddit <span className="text-red-400">*</span></label>
                    <input value={redditSubreddit} onChange={e => setRedditSubreddit(e.target.value)} placeholder="ex: r/empreendedorismo" className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-1.5 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500" />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Titulo do post <span className="text-red-400">*</span></label>
                    <input value={redditTitle} onChange={e => setRedditTitle(e.target.value)} placeholder="Titulo obrigatorio para o Reddit" className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-1.5 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500" />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">ID do flair (opcional)</label>
                    <input value={redditFlair} onChange={e => setRedditFlair(e.target.value)} placeholder="ID do flair do subreddit" className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-1.5 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500" />
                  </div>
                </div>
              )}

              {/* Pinterest settings */}
              {uniquePlatforms.includes('pinterest') && (
                <div className="mt-3 rounded-xl border border-slate-700 bg-slate-800/50 p-3 space-y-3">
                  <p className="text-xs font-semibold text-slate-300">Pinterest</p>
                  <div>
                    <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">ID do quadro <span className="text-red-400">*</span></label>
                    <input value={pinterestBoardId} onChange={e => setPinterestBoardId(e.target.value)} placeholder="ID do board no Pinterest" className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-1.5 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500" />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Titulo do pin</label>
                    <input value={pinterestTitle} onChange={e => setPinterestTitle(e.target.value)} placeholder="Titulo do pin" className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-1.5 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500" />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Link de destino (URL)</label>
                    <input value={pinterestLink} onChange={e => setPinterestLink(e.target.value)} placeholder="https://seusite.com/pagina" className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-1.5 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500" />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">ID da secao do quadro (opcional)</label>
                    <input value={pinterestBoardSection} onChange={e => setPinterestBoardSection(e.target.value)} placeholder="ID da secao" className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-1.5 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500" />
                  </div>
                </div>
              )}

              {/* TikTok avancado */}
              {uniquePlatforms.includes('tiktok') && (
                <div className="mt-3 rounded-xl border border-slate-700 bg-slate-800/50 p-3 space-y-3">
                  <p className="text-xs font-semibold text-slate-300">TikTok</p>
                  <div>
                    <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Privacidade</label>
                    <select value={tiktokPrivacy} onChange={e => setTiktokPrivacy(e.target.value as any)} className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-1.5 text-xs text-slate-200 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                      <option value="PUBLIC_TO_EVERYONE">Publico</option>
                      <option value="MUTUAL_FOLLOW_FRIENDS">Amigos (seguidos mutuos)</option>
                      <option value="SELF_ONLY">Privado (so eu)</option>
                    </select>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-400">Permitir Dueto</span>
                    <button onClick={() => setTiktokDuet(v => !v)} className={`relative h-5 w-9 rounded-full transition-colors ${tiktokDuet ? 'bg-indigo-600' : 'bg-slate-700'}`}>
                      <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${tiktokDuet ? 'translate-x-4' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-400">Permitir Stitch</span>
                    <button onClick={() => setTiktokStitch(v => !v)} className={`relative h-5 w-9 rounded-full transition-colors ${tiktokStitch ? 'bg-indigo-600' : 'bg-slate-700'}`}>
                      <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${tiktokStitch ? 'translate-x-4' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-xs text-slate-400">Declarar conteudo patrocinado</span>
                      <p className="text-[9px] text-slate-600">Obrigatorio para posts pagos/parceria</p>
                    </div>
                    <button onClick={() => setTiktokDisclose(v => !v)} className={`relative h-5 w-9 rounded-full transition-colors ${tiktokDisclose ? 'bg-indigo-600' : 'bg-slate-700'}`}>
                      <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${tiktokDisclose ? 'translate-x-4' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                  {tiktokDisclose && (
                    <div className="pl-2 space-y-2 border-l border-slate-700">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-400">Conteudo de marca (Brand Content)</span>
                        <button onClick={() => setTiktokBrandContent(v => !v)} className={`relative h-5 w-9 rounded-full transition-colors ${tiktokBrandContent ? 'bg-indigo-600' : 'bg-slate-700'}`}>
                          <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${tiktokBrandContent ? 'translate-x-4' : 'translate-x-0.5'}`} />
                        </button>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-400">Organico de marca (Brand Organic)</span>
                        <button onClick={() => setTiktokBrandOrganic(v => !v)} className={`relative h-5 w-9 rounded-full transition-colors ${tiktokBrandOrganic ? 'bg-indigo-600' : 'bg-slate-700'}`}>
                          <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${tiktokBrandOrganic ? 'translate-x-4' : 'translate-x-0.5'}`} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Instagram thumbnail para Reels */}
              {uniquePlatforms.includes('instagram') && getPostType('instagram') === 'reels' && (
                <div className="mt-3 rounded-xl border border-slate-700 bg-slate-800/50 p-3">
                  <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Thumbnail do Reel (URL, opcional)</label>
                  <input value={instagramThumbnail} onChange={e => setInstagramThumbnail(e.target.value)} placeholder="https://cdn.seusite.com/thumb.jpg" className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-1.5 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500" />
                </div>
              )}

              {/* Avisos de formato por plataforma */}
              {(() => {
                const warnings = uniquePlatforms.flatMap(platform => {
                  const type = getPostType(platform);
                  const msg = FORMAT_WARNINGS[platform]?.[type];
                  return msg ? [{ platform, msg }] : [];
                });
                if (warnings.length === 0) return null;
                return (
                  <div className="mt-3 space-y-1.5">
                    {warnings.map(({ platform, msg }) => (
                      <div key={platform} className="flex gap-2 rounded-lg bg-amber-900/20 border border-amber-700/30 px-3 py-2">
                        <span className="text-amber-400 text-xs shrink-0">Aviso</span>
                        <p className="text-[11px] text-amber-300/80">
                          <span className="font-semibold capitalize">{platform}:</span> {msg}
                        </p>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Caption */}
          <div className="lg:col-span-2">
            <label className="block text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Caption</label>
            <textarea
              value={caption}
              onChange={e => setCaption(e.target.value)}
              rows={5}
              placeholder="Legenda gerada pela IA..."
              className="w-full resize-none rounded-xl border border-slate-700 bg-slate-800 px-3 py-2.5 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <p className="mt-1 text-right text-xs text-slate-600">{caption.length}/2200</p>
          </div>

          {needsTikTokCaption && (
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Caption TikTok</label>
              <textarea
                value={tiktokCaption}
                onChange={e => setTiktokCaption(e.target.value.slice(0, 90))}
                rows={2}
                maxLength={90}
                placeholder="Versao curta somente para TikTok..."
                className="w-full resize-none rounded-xl border border-slate-700 bg-slate-800 px-3 py-2.5 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <p className="mt-1 text-right text-xs text-slate-600">{tiktokCaption.length}/90</p>
            </div>
          )}

          {/* Hashtags */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Hashtags</label>
            <input
              type="text"
              value={hashtags}
              onChange={e => setHashtags(e.target.value)}
              placeholder="#exemplo #marketing #conteudo"
              className="w-full rounded-xl border border-slate-700 bg-slate-800 px-3 py-2.5 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          {/* Automacao comentario para DM - so aparece se IG ou FB selecionados */}
          {uniquePlatforms.some(p => ['instagram', 'facebook'].includes(p)) && <div className="rounded-xl border border-slate-700 bg-slate-800/50 p-4">
            <div className="flex items-center justify-between mb-1">
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Automacao Comentario para DM</p>
                <p className="text-[10px] text-slate-600 mt-0.5">Instagram e Facebook apenas</p>
              </div>
              <button
                onClick={() => setAutoEnabled(v => !v)}
                className={`relative h-5 w-9 rounded-full transition-colors ${autoEnabled ? 'bg-indigo-600' : 'bg-slate-700'}`}
              >
                <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${autoEnabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
              </button>
            </div>
            {autoEnabled && (
              <div className="mt-3 space-y-3">
                <div>
                  <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Keywords (separadas por virgula)</label>
                  <input
                    type="text"
                    value={autoKeywords}
                    onChange={e => setAutoKeywords(e.target.value)}
                    placeholder="QUERO, EU QUERO, INFO, LINK"
                    className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </div>
                {!followerCheckEnabled && <div>
                  <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Mensagem do DM</label>
                  <textarea
                    rows={3}
                    value={autoDmMessage}
                    onChange={e => setAutoDmMessage(e.target.value)}
                    maxLength={DM_TEXT_LIMIT}
                    placeholder="Ola! Aqui esta o link que voce pediu: https://..."
                    className="mt-1 w-full resize-none rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                  <p className="text-[9px] text-slate-600 mt-0.5">Coloque aqui o link, cupom ou entregavel - nunca na resposta publica.</p>
                </div>}
                <div className="rounded-lg border border-slate-700 bg-slate-900/70 p-3">
                  <label className="flex items-start gap-2 text-xs font-semibold text-slate-300">
                    <input
                      type="checkbox"
                      checked={followerCheckEnabled}
                      onChange={e => setFollowerCheckEnabled(e.target.checked)}
                      disabled={!selectedPlatforms.includes('instagram')}
                      className="mt-0.5 rounded border-slate-600 bg-slate-800 text-indigo-600 disabled:opacity-50"
                    />
                    <span>
                      Checar se e seguidor
                      <span className="block text-[10px] font-normal text-slate-500">Quando o lead responder o texto abaixo no direct, verificamos instagramProfile.isFollower e enviamos a mensagem certa.</span>
                    </span>
                  </label>
                  {!selectedPlatforms.includes('instagram') && (
                    <p className="mt-2 rounded bg-amber-900/20 px-2 py-1.5 text-[10px] text-amber-300">Disponivel somente para Instagram.</p>
                  )}
                  {followerCheckEnabled && selectedPlatforms.includes('instagram') && (
                    <div className="mt-3 space-y-2">
                      <div>
                        <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Mensagem que acompanha o botao</label>
                        <textarea
                          rows={2}
                          value={followerCheckPromptMessage}
                          onChange={e => setFollowerCheckPromptMessage(e.target.value)}
                          maxLength={DM_TEXT_LIMIT}
                          className="mt-1 w-full resize-none rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-200 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Texto do botao</label>
                        <input
                          type="text"
                          value={followerCheckButtonText}
                          onChange={e => setFollowerCheckButtonText(e.target.value)}
                          className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-200 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Mensagem para quem segue</label>
                        <textarea
                          rows={2}
                          value={followerCheckFollowerMessage}
                          onChange={e => setFollowerCheckFollowerMessage(e.target.value)}
                          maxLength={DM_TEXT_LIMIT}
                          className="mt-1 w-full resize-none rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-200 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Mensagem para quem nao segue</label>
                        <textarea
                          rows={2}
                          value={followerCheckNonFollowerMessage}
                          onChange={e => setFollowerCheckNonFollowerMessage(e.target.value)}
                          maxLength={DM_TEXT_LIMIT}
                          className="mt-1 w-full resize-none rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-200 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                        <p className="mt-1 text-[9px] text-slate-600">O mesmo botao inicial sera enviado junto para a pessoa tentar novamente depois de seguir.</p>
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Resposta no comentario (prova social)</label>
                  <input
                    type="text"
                    value={autoCommentReply}
                    onChange={e => setAutoCommentReply(e.target.value)}
                    placeholder="Te chamei no Direct!"
                    className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Nome interno da automacao</label>
                  <input
                    type="text"
                    value={autoName}
                    onChange={e => setAutoName(e.target.value)}
                    placeholder="Ex: Lead Magnet Campanha Mai/26"
                    className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </div>
                {mode === 'schedule' && (
                  <p className="text-[10px] text-amber-400/80 bg-amber-900/20 rounded-lg px-3 py-2">
                    Post agendado: a automacao sera criada automaticamente quando o post publicar. Use "Materializar Automacoes" se precisar forcar.
                  </p>
                )}
              </div>
            )}
          </div>}

          {/* Quando publicar */}
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">Quando publicar</p>
            <div className="flex gap-2 mb-3">
              <button
                onClick={() => setMode('now')}
                className={`flex-1 rounded-xl border py-2 text-sm font-semibold transition-all ${
                  mode === 'now'
                    ? 'border-emerald-500 bg-emerald-900/30 text-emerald-300'
                    : 'border-slate-700 bg-slate-800 text-slate-400 hover:border-slate-600'
                }`}
              >
                Publicar agora
              </button>
              <button
                onClick={() => setMode('schedule')}
                className={`flex-1 rounded-xl border py-2 text-sm font-semibold transition-all ${
                  mode === 'schedule'
                    ? 'border-indigo-500 bg-indigo-900/30 text-indigo-300'
                    : 'border-slate-700 bg-slate-800 text-slate-400 hover:border-slate-600'
                }`}
              >
                Agendar
              </button>
            </div>
            {mode === 'schedule' && (
              <input
                type="datetime-local"
                value={scheduledAt}
                onChange={e => setScheduledAt(e.target.value)}
                className="w-full rounded-xl border border-slate-700 bg-slate-800 px-3 py-2.5 text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-slate-800 px-5 py-4">
          <p className="text-xs text-slate-600">
            {selected.size} conta{selected.size !== 1 ? 's' : ''}
            {isCarousel && mediaCount > 0 && ` · ${mediaCount} slides`}
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="rounded-xl border border-slate-700 px-4 py-2 text-sm text-slate-400 hover:text-slate-200"
            >
              Cancelar
            </button>
            <button
              onClick={handlePublish}
              disabled={publishing || selected.size === 0}
              className={`rounded-xl px-5 py-2 text-sm font-semibold text-white disabled:opacity-40 transition-colors ${
                mode === 'now' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-indigo-600 hover:bg-indigo-700'
              }`}
            >
              {publishing
                ? 'Enviando...'
                : mode === 'now' ? 'Publicar agora' : 'Agendar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function isVideoUrl(url: string | null | undefined): boolean {
  return /\.(mp4|webm|mov)(\?|$)/i.test(url || '');
}

interface PreviewItem { url: string; title: string; }

function AssetPreviewModal({ items, initialIndex, onClose }: { items: PreviewItem[]; initialIndex: number; onClose: () => void }) {
  const [idx, setIdx] = useState(initialIndex);
  const item = items[idx];
  const video = isVideoUrl(item?.url);
  const hasPrev = idx > 0;
  const hasNext = idx < items.length - 1;

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft' && hasPrev) setIdx(i => i - 1);
    if (e.key === 'ArrowRight' && hasNext) setIdx(i => i + 1);
    if (e.key === 'Escape') onClose();
  };

  if (!item) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4"
      onClick={onClose}
      onKeyDown={handleKey}
      tabIndex={-1}
    >
      <div onClick={e => e.stopPropagation()} className="relative flex flex-col items-center w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center justify-between w-full mb-3">
          <p className="text-white/60 text-xs">{items.length > 1 ? `${idx + 1} / ${items.length}` : ''}</p>
          <p className="text-white text-sm font-semibold truncate max-w-xs text-center flex-1">{item.title}</p>
          <button onClick={onClose} className="text-white/60 hover:text-white text-sm ml-2">Fechar</button>
        </div>

        {/* Media */}
        <div className="relative w-full flex items-center justify-center">
          {hasPrev && (
            <button
              onClick={() => setIdx(i => i - 1)}
              className="absolute left-0 z-10 -translate-x-full mr-2 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 hover:bg-white/25 text-white text-xl transition-colors"
            >
              ‹
            </button>
          )}

          {video ? (
            <video key={item.url} src={item.url} controls autoPlay playsInline className="w-full rounded-xl shadow-2xl" style={{ maxHeight: '80vh' }} />
          ) : (
            <img key={item.url} src={item.url} alt={item.title} className="w-full rounded-xl shadow-2xl object-contain" style={{ maxHeight: '80vh' }} />
          )}

          {hasNext && (
            <button
              onClick={() => setIdx(i => i + 1)}
              className="absolute right-0 z-10 translate-x-full ml-2 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 hover:bg-white/25 text-white text-xl transition-colors"
            >
              ›
            </button>
          )}
        </div>

        {/* Dots */}
        {items.length > 1 && (
          <div className="flex gap-1.5 mt-3">
            {items.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-5 bg-white' : 'w-1.5 bg-white/30 hover:bg-white/50'}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function AssetGallery({ assets, jobId: _jobId, captionSeo, onRefresh }: Props) {
  const [publishTarget, setPublishTarget] = useState<Asset | null>(null);
  const [preview, setPreview] = useState<{ items: PreviewItem[]; initialIndex: number } | null>(null);
  const allAssets = assets;

  if (assets.length === 0) return null;

  const mainAssets = assets.filter(a => !['carousel_slides', 'post_estatico_variant', 'carrossel_variant'].includes(a.type));
  const variantAssets = assets.filter(a => ['carousel_slides', 'post_estatico_variant', 'carrossel_variant'].includes(a.type));

  // 'video' é tipo de ASSET (vídeo avulso), não deliveryType — por isso fica fora do registro.
  const isVideo = (type: string) => isVideoDeliveryType(type) || type === 'video';

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm font-bold text-slate-700">Assets gerados ({assets.length})</p>
        <span className="text-xs text-slate-400">Selecione para publicar em uma plataforma</span>
      </div>

      {/* Assets principais */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {mainAssets.map(asset => (
          <div key={asset.id} className="group overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
            {/* Preview */}
            {asset.previewUrl && !isVideo(asset.type) && (
              <button
                onClick={() => setPreview({ items: [{ url: asset.url || asset.previewUrl!, title: asset.title }], initialIndex: 0 })}
                className="block w-full focus:outline-none"
              >
                <img
                  src={asset.previewUrl}
                  alt={asset.title}
                  className="h-48 w-full object-cover group-hover:opacity-95 transition-opacity"
                />
              </button>
            )}
            {asset.url && isVideo(asset.type) && (
              <button
                onClick={() => setPreview({ items: [{ url: asset.url!, title: asset.title }], initialIndex: 0 })}
                className="block w-full focus:outline-none"
              >
                <div
                  className="relative overflow-hidden bg-slate-900 text-white"
                  style={{ aspectRatio: '9/16', maxHeight: 220 }}
                >
                  <video
                    src={asset.url}
                    muted
                    playsInline
                    preload="metadata"
                    className="h-full w-full object-cover opacity-80"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-t from-black/50 to-black/5">
                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-black/55 text-sm font-bold text-white shadow-lg">Play</span>
                  </div>
                </div>
              </button>
            )}
            {!asset.previewUrl && !asset.url && (
              <div className="flex h-32 items-center justify-center bg-slate-100 text-slate-400 text-2xl">
                {isVideo(asset.type) ? 'Video' : 'Imagem'}
              </div>
            )}

            <div className="p-3 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-semibold text-slate-800 leading-tight">{asset.title}</p>
                <span className={`flex-shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                  asset.status === 'draft' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'
                }`}>
                  {asset.status}
                </span>
              </div>
              <p className="text-xs text-slate-500">{TYPE_LABELS[asset.type] || asset.type}</p>
              {asset.caption && (
                <p className="text-xs text-slate-400 line-clamp-2">{asset.caption}</p>
              )}
              <button
                onClick={() => setPublishTarget(asset)}
                className="w-full rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 transition-colors"
              >
                Publicar em...
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Slides / variacoes */}
      {variantAssets.length > 0 && (
        <div>
          {variantAssets.map(asset => {
            // Coleta todas as URLs dos slides - variantUrls OU slideAssets[].url
            const urlsFromVariants: string[] = asset.manifest?.variantUrls || [];
            const urlsFromSlides: string[] = (asset.manifest?.slideAssets || []).map((s: any) => s.url).filter(Boolean);
            const allUrls = urlsFromVariants.length > 0 ? urlsFromVariants : urlsFromSlides;
            const slideAssets: any[] = asset.manifest?.slideAssets || [];

            return (
              <div key={asset.id}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-slate-500">
                    {asset.type === 'carousel_slides' ? `Slides do carrossel` : 'Variacoes'} ({allUrls.length})
                  </p>
                </div>
                {allUrls.length === 0 ? (
                  <p className="text-xs text-slate-500 italic">Slides ainda sendo gerados...</p>
                ) : (
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {allUrls.map((url, i) => {
                      const slide = slideAssets[i];
                      const slideItems: PreviewItem[] = allUrls.map((u, j) => ({
                        url: u,
                        title: slideAssets[j]?.copyLine || `Slide ${j + 1}`,
                      }));
                      return (
                        <button
                          key={i}
                          onClick={() => setPreview({ items: slideItems, initialIndex: i })}
                          className="flex-shrink-0 group/slide focus:outline-none"
                        >
                          <div className="relative">
                            <img
                              src={url}
                              alt={`Slide ${i + 1}`}
                              className="h-32 w-24 rounded-lg border border-slate-700 object-cover hover:opacity-90 transition-opacity"
                            />
                            <span className="absolute bottom-1 left-1 rounded bg-black/60 px-1 text-[9px] text-white">{slide?.slideNumber ?? i + 2}</span>
                          </div>
                          {slide?.copyLine && (
                            <p className="mt-1 w-24 text-[9px] text-slate-500 truncate">{slide.copyLine}</p>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {publishTarget && (
        <PublishModal
          asset={publishTarget}
          allAssets={allAssets}
          captionSeo={captionSeo}
          onClose={() => setPublishTarget(null)}
          onPublished={onRefresh}
        />
      )}

      {preview && (
        <AssetPreviewModal
          items={preview.items}
          initialIndex={preview.initialIndex}
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  );
}


